import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, MessagesSquare, Plus } from 'lucide-react';
import '@speles7172/message-console/styles.css';
import { THREAD_ENTITY_TYPES, threadLinkLabel, threadTopic } from '@peles/shared';
import { EntityList, FilterSelect, StatusBadge } from '../components/common';
import type { EntityListColumn } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { NewConversationModal } from '../components/messaging/NewConversationModal';
import { ConversationView } from '../components/messaging/ConversationView';
import { useAuth } from '../context/AuthContext';
import { useCan } from '../hooks/useCan';
import { fetchConversations, type ConversationRow } from '../api/conversations';
import { entityRoute } from '../components/messaging/entityRoutes';
import { chipTone, relativeTime } from '../components/messaging/conversationFormat';
import { useInTabbedPage } from '../components/common/TabbedPage';

/**
 * Every conversation in one table (COMMUNICATIONS-DESIGN H2).
 *
 * ⚠️ **Our table over the package's data, not `<NotificationsConsole>`.**
 * CLAUDE.md's line: a screen that *is* the package's renders whole — the audit
 * log, the SQL console — and this one is not. It sits beside `EntityList`
 * everywhere else in the app, so it uses `EntityList`: the sorting, the paging,
 * the mobile card rows and the skeleton are already solved here, and a second
 * table idiom on one screen reads as a bug rather than a choice.
 *
 * ⚠️ **The rows are the caller's own conversations, and the server decides
 * that.** There is no participant filter that widens the set — `withParticipant`
 * narrows *within* it to threads somebody else is also on.
 *
 * ⚠️ **A thread opens in a modal, not on a page of its own.** Somebody scanning
 * a hundred conversations for the one they half-remember opens several; a route
 * per thread makes each one a navigation and a return, and loses the filters
 * they set on the way in.
 */
const PAGE_SIZE = 25;

const STATUSES = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
];

// Shared with the record-scoped list, so a person reads the same on both.
export { chipTone, relativeTime };

export function CommunicationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // The open conversation lives in the address (`?thread=`), so a link — from
  // quick search, a reload, the back button — lands on it.
  const [params, setParams] = useSearchParams();
  const openThreadId = params.get('thread');
  const setOpenThreadId = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('thread', id);
        else next.delete('thread');
        return next;
      },
      { replace: !id },
    );
  const [newOpen, setNewOpen] = useState(false);
  const canStart = useCan('conversations.start');
  const embedded = useInTabbedPage();

  const query = useMemo(
    () => ({
      // The package filters one entity type at a time, which is why this pill
      // keeps only the last choice rather than accumulating.
      ...(entityTypes[0] ? { entityType: entityTypes[0] } : {}),
      ...(statuses.length > 0 ? { statuses } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(from ? { from } : {}),
      // Inclusive to the end of the chosen day: "to the 1st" means the whole of
      // the 1st, not midnight at its start, and the other reading silently
      // hides a day's conversations.
      ...(to ? { to: `${to}T23:59:59.999Z` } : {}),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [entityTypes, statuses, search, from, to, page],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['conversations', query],
    queryFn: () => fetchConversations(query),
  });

  const rows = data?.items ?? [];

  const columns: EntityListColumn<ConversationRow>[] = useMemo(
    () => [
      {
        key: 'topic',
        header: 'Topic',
        render: (row) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              {(row.unreadCount ?? 0) > 0 && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-primary-600"
                  title="Unread"
                  data-testid="unread-dot"
                />
              )}
              <span
                className={`truncate ${
                  (row.unreadCount ?? 0) > 0
                    ? 'font-semibold text-gray-900'
                    : 'font-medium text-gray-800'
                }`}
              >
                {threadTopic(row.entityType, row.entityId, row.entityName)}
              </span>
            </div>
            {row.subject && <p className="truncate text-xs text-gray-500">{row.subject}</p>}
          </div>
        ),
      },
      {
        key: 'link',
        header: 'Link',
        render: (row) => {
          const target = entityRoute(row.entityType, row.entityId);
          const label = threadLinkLabel(row.entityType);
          // A chip that cannot go anywhere is still worth rendering — it says
          // what the conversation is about — but it is not a link.
          return target === null ? (
            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              {label}
            </span>
          ) : (
            <button
              type="button"
              onClick={(event) => {
                // The row opens the thread; the chip goes to the record.
                event.stopPropagation();
                navigate(target);
              }}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-100"
            >
              {label}
              <ExternalLink size={11} />
            </button>
          );
        },
      },
      {
        key: 'participants',
        header: 'Participants',
        render: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.participants.map((participant) => (
              <span
                key={participant.recipientId}
                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${chipTone(
                  participant.recipientId,
                )}`}
              >
                {participant.name ?? '—'}
                {participant.recipientId === user?.id ? ' (you)' : ''}
              </span>
            ))}
          </div>
        ),
      },
      {
        key: 'updatedAt',
        header: 'Last response',
        render: (row) => (
          <div className="whitespace-nowrap">
            <div className="text-gray-700">{relativeTime(row.updatedAt)}</div>
            {row.previewSender && (
              <div className="text-xs text-gray-400">
                by <span className="font-medium text-gray-600">{row.previewSender}</span>
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusBadge label={row.status} />,
        width: 'w-28',
      },
    ],
    [navigate, user?.id],
  );

  /*
   * ⚠️ **The same conversation, drawn the same way wherever it is opened.**
   * `ConversationView` is what a record's own panel renders too: the same
   * attachments and upload, the same people for its record, and the same
   * tools beside it — attach from the library, attach a report, ask for a file
   * back — which this page used to leave out.
   */

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        {/* Inside the Communication page the heading above is the page's. */}
        {!embedded && (
          <>
            <MessagesSquare className="text-primary-600" size={22} />
            <h1 className="text-2xl font-bold text-gray-900">Communication</h1>
          </>
        )}
        {/*
          ⚠️ **The page listed conversations and could not start one.** Every
          conversation had to begin on a record, so the ones that belong to no
          record — ringing a venue about next season, a question to the office —
          had nowhere to begin at all. The dialog offers an event and lets it be
          left empty, which is the same thing `POST /threads` has always
          accepted.
        */}
        {canStart && (
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700"
            data-testid="new-conversation"
          >
            <Plus size={15} />
            New conversation
          </button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search conversations…"
          className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          aria-label="Search conversations"
        />
        <FilterSelect
          label="Link type"
          items={[...THREAD_ENTITY_TYPES]}
          value={entityTypes}
          onChange={(value) => {
            setEntityTypes(Array.isArray(value) ? value.slice(-1) : [value]);
            setPage(1);
          }}
          getValue={(item) => item.key}
          getLabel={(item) => item.label}
        />
        <FilterSelect
          label="Status"
          items={STATUSES}
          value={statuses}
          onChange={(value) => {
            setStatuses(Array.isArray(value) ? value : [value]);
            setPage(1);
          }}
          getValue={(item) => item.id}
          getLabel={(item) => item.label}
          multiple
        />
        <label className="flex items-center gap-1 text-xs text-gray-500">
          From
          <input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            aria-label="From date"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          To
          <input
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            aria-label="To date"
          />
        </label>
      </div>

      <EntityList
        items={rows}
        columns={columns}
        getKey={(row) => row.id}
        onRowClick={(row) => setOpenThreadId(row.id)}
        isLoading={isLoading}
        emptyMessage="No conversations yet."
        page={page}
        pageSize={PAGE_SIZE}
        total={data?.total ?? 0}
        onPageChange={setPage}
      />

      <EntityModal
        open={openThreadId !== null}
        onClose={() => setOpenThreadId(null)}
        title="Conversation"
        size="lg"
      >
        {openThreadId && <ConversationView threadId={openThreadId} />}
      </EntityModal>

      <NewConversationModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(threadId) => setOpenThreadId(threadId)}
      />
    </div>
  );
}
